import { useEffect, useContext, useRef } from 'react'
import Head from 'next/head'

import Conversation from '../../components/Conversation/Conversation'
import Quote from '../../components/Quote/Quote'
import Table from '../../components/Table/Table'
import Text from '../../components/Text/Text'
import ThemeHeader from '../../components/ThemeHeader/ThemeHeader'
import ImageBlock from '../../components/ImageBlock/ImageBlock'
import VideoBlock from '../../components/VideoBlock/VideoBlock'
import Footer from '../../components/Footer/Footer'
import Gallery from '../../components/Gallery/Gallery'
import ModalVideo from 'react-modal-video'
import parseBreaks from '../../utils/parseBreaks'

// context
import { PageTransitionContext } from '../../context/PageTransitionContext'
import { VideoModalContext } from '../../context/VideoModalContext'

// Page 404
import Custom404 from '../404'
import Seo from '../../components/Seo/Seo'
import CardList from '../../components/CardList/CardList'
import LinkCard from '../../components/LinkCard/LinkCard'
import ThemeCard from "../../components/ThemeCard/ThemeCard";

const Theme = (props) => {

  if(props.page.error) {
    return <Custom404 />
  }

  const { themes } = props

  // parse metadata fields
  const metadata = {
    ...JSON.parse(props.page.seo.metaTitleContainer).title,
    ...JSON.parse(props.page.seo.metaTagContainer)
  }

  // context
  const transitionContextState = useContext(PageTransitionContext)
  const videoModalContext = useContext(VideoModalContext)

  // refs
  const pageContainerRef = useRef()

  useEffect( ()=> {
    // hide page when vision theme changes (navigating from one vision detail to other vision detail)
    transitionContextState.setShowPage(false)
  }, [themes])

  useEffect(()=>{

    if (props.page.header[0].color !== '') {
      transitionContextState.setStyle(props.page.header[0].color)
      transitionContextState.setPageType('vision')
      transitionContextState.setAnimatedTitle(parseBreaks(props.page.header[0].title))
      transitionContextState.setPageTransition(true)
    } else {
      transitionContextState.setShowPage(true)
    }

    return () =>{
      transitionContextState.setShowPage(false)
    }

  },[themes])

  useEffect(() => {
    if (transitionContextState.showPage){
      pageContainerRef.current.classList.add('is-visible')
    } else {
      pageContainerRef.current.classList.remove('is-visible')
    }
  }, [transitionContextState.showPage])

  return (
    <div className="PageContainer" ref={pageContainerRef}>
      <Seo {...metadata}/>

      <ThemeHeader title={props.page.title} content={props.page.header[0]}/>

      {props.page.components.map((component, index) => {
        switch (component.type) {
          case 'text':
            return (
              <Text key={`${component.type}-${index}`} {...component} />
            )
          case 'gallery':
            return (
              <Gallery key={`${component.type}-${index}`} {...component} />
            )
          case 'table':
            return (
              <Table {...component} key={`${component.type}-${index}`}/>
            )
          case 'image':
            return (
              <ImageBlock {...component} key={`${component.type}-${component.id}`}/>
            )
          case 'video':
            return (
              <VideoBlock key={`${component.type}-${component.id}`} {...component} />
            )
          case 'quote':
            return (
              <Quote key={`${component.type}-${component.id}`} {...component} />
            )
          case 'conversation' :
            return (
              <Conversation key={`${component.type}-${component.id}`} {...component} />
            )
          case 'referral':
            return (
              <CardList layout={`stacked`} key={`CardList-${component.id}`}>
                <LinkCard {...component} key={`LinkCard-${component.id}-${index}`}/>
              </CardList>
            )
        }
      })}
      <ModalVideo channel={videoModalContext.videoChannel} isOpen={videoModalContext.videoOpen} videoId={videoModalContext.videoId} onClose={() => videoModalContext.closeVideoModal() } />

      <CardList layout={`spread`} type="themes">

        {themes.map((card, index) => {

          if (card.slug === props.slug) {
            return false
          }
          return <ThemeCard key={`ThemeCard-${card.id}-${index}`} url={`/${card.uri}`} color={card.header[0].color}
                            content={{ label: card.title, title: card.header[0].title }}
                            image={{ alt: card.header[0].curator[0].name, url: card.header[0].curatorImage[0].url }}/>
        })}
      </CardList>

      <Footer/>
    </div>
  )
}

export async function getServerSideProps (context) {
  const query = require('../../queries/gqlTheme.graphql')

  // api endpoint
  let apiUrl = process.env.API_URL

  const queryData = {
    variables: context.params,
    query
  }

  const opts = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(queryData)
  }

  // update api url to enable live and share preview
  if (context.query) {
    if (context.query.token && !!(context.query['x-craft-live-preview'] || context.query['x-craft-preview'])) {
      apiUrl = `${process.env.API_URL}?token=${context.query['token']}`
    }
  }

  const res = await fetch(apiUrl, opts)
  const json = await res.json()



  return {
    props: {
      page: json.data.entry || { error : true }, // will be passed to the page component as props
      themes: json.data.themes,
      slug: context.query.slug
    }
  }
}

export default Theme
